import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { permissionService } from '../services/api';
import { useAuth } from './AuthContext';

const PermissionContext = createContext(null);

export const PermissionProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const lastUpdatedRef = useRef(null);

  const loadPermissions = useCallback(async () => {
    if (!user || !token) {
      setPermissions({});
      setLoading(false);
      return;
    }

    try {
      const response = await permissionService.getMyPermissions();
      setPermissions(response.data);
      lastUpdatedRef.current = Date.now();
    } catch (error) {
      console.error('Erreur chargement permissions:', error);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Vérifier si les permissions ont été modifiées par l'admin et recharger si nécessaire
  useEffect(() => {
    if (!user || !token) return;

    const checkForUpdates = () => {
      const updatedAt = localStorage.getItem('permissions_updated_at');
      if (updatedAt && lastUpdatedRef.current && parseInt(updatedAt) > lastUpdatedRef.current) {
        console.log('Permissions modifiées par admin, rechargement...');
        loadPermissions();
      }
    };

    // Vérifier toutes les 30 secondes
    const interval = setInterval(checkForUpdates, 30000);
    
    // Vérifier aussi quand la fenêtre reprend le focus
    const handleFocus = () => checkForUpdates();
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, token, loadPermissions]);

  // Vérifier si l'utilisateur a une permission spécifique
  const hasPermission = useCallback((permissionKey) => {
    if (!permissionKey) return true;
    return permissions[permissionKey] === true;
  }, [permissions]);

  // Vérifier si l'utilisateur a au moins une des permissions
  const hasAnyPermission = useCallback((permissionKeys) => {
    if (!permissionKeys || permissionKeys.length === 0) return true;
    return permissionKeys.some(key => permissions[key] === true);
  }, [permissions]);

  // Vérifier si l'utilisateur a toutes les permissions
  const hasAllPermissions = useCallback((permissionKeys) => {
    if (!permissionKeys || permissionKeys.length === 0) return true;
    return permissionKeys.every(key => permissions[key] === true);
  }, [permissions]);

  // Recharger les permissions (utile après modification par admin)
  const refreshPermissions = useCallback(() => {
    setLoading(true);
    loadPermissions();
  }, [loadPermissions]);

  const value = {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

// Hook pour utiliser les permissions
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Composant pour afficher/masquer du contenu selon les permissions
export const PermissionGate = ({ permission, permissions, requireAll = false, fallback = null, children }) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = usePermissions();

  if (loading) {
    return null;
  }

  // Vérification d'une seule permission
  if (permission) {
    return hasPermission(permission) ? children : fallback;
  }

  // Vérification de plusieurs permissions
  if (permissions && permissions.length > 0) {
    if (requireAll) {
      return hasAllPermissions(permissions) ? children : fallback;
    }
    return hasAnyPermission(permissions) ? children : fallback;
  }

  return children;
};

export default PermissionContext;
